import { Alert, IconButton, Typography } from '@mui/joy';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { useContext, useMemo } from 'react';
import lowerFirst from 'lodash/lowerFirst';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { AppContext } from '@/shared/context/context';
import { ApplicationAccess } from '~/common/enums';
import { trpc } from '@/config/trpc';

import { useUserRole } from '@/queries';

import { OpenIcon, WarningIcon } from '@/shared/icons/Icons';
import CaseTaskWidget from './Widgets/CaseTaskWidget';
import DashboardHeader from './Widgets/DashboardHeader';
import HomeScoreWidget from './Widgets/HomeScoreWidget';
import InactiveIntegrations from './Widgets/InactiveIntegrations';
import KpcFullPage from '@/shared/components/2.0/layout/KpcFullPage';
import TabsHashMenu from '@/shared/components/2.0/layout/Tabs/router/TabsHashMenu';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DashboardTitle = () => {
  const { t } = useTranslation();

  const auth = useAuth();

  return (
    <Typography
      className="mb-4 text-2xl font-medium"
      level="h1"
      startDecorator={
        <Typography className="text-2xl" level="h1">
          👋
        </Typography>
      }
    >
      {t('dashboard.title', { name: auth.user?.profile?.name })}
    </Typography>
  );
};

const Dashboard = () => {
  const { currentTeam } = useContext(AppContext);
  const { isSupervisor } = useUserRole();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const hasRepairAccess =
    currentTeam?.applicationAccess.includes(ApplicationAccess.VeraRepair) ?? false;

  const { data: taskTypes } = trpc.case.repair.getRepairTaskCount.useQuery(undefined, {
    enabled: !!hasRepairAccess,
  });
  const { data: emailAccounts } = trpc.mailbox.accounts.list.useQuery();

  const oldAccounts = useMemo(() => {
    if (!emailAccounts) return [];

    return emailAccounts.filter((ea) => !ea.isNewAccount || !ea.isValid);
  }, [emailAccounts]);

  const tabs = taskTypes?.map((type) => ({
    badgeContent: type.repairCasesCount,
    content: (
      <CaseTaskWidget
        filters={{
          systemTaskType: type.systemTaskType,
          teamTaskTypeId: type.teamTaskTypeId,
        }}
      />
    ),
    hash: `tasks-${type.systemTaskType.toLowerCase()}`,
    labelKey: lowerFirst(type.systemTaskType),
  }));

  const handleNavigateToRelink = () => {
    if (oldAccounts.some((ea) => ea.isTeamMailbox) && isSupervisor) {
      navigate('/team/settings/communication');
      return;
    }
    if (oldAccounts.every((ea) => !ea.isTeamMailbox)) navigate('/user/settings/email-accounts');
  };

  return (
    <KpcFullPage>
      <DashboardTitle />
      <DashboardHeader />
      {!!oldAccounts.length && (
        <Alert
          className="w-50 my-2"
          color="danger"
          endDecorator={
            isSupervisor || oldAccounts.some((ea) => !ea.isTeamMailbox) ? (
              <IconButton color="neutral" variant="plain" onClick={handleNavigateToRelink}>
                <OpenIcon />
              </IconButton>
            ) : undefined
          }
          size="lg"
          startDecorator={<WarningIcon />}
        >
          {t('followingAccountsNeedToBeRelinked', {
            accounts: oldAccounts.map((x) => x.emailAddress).join(', '),
          })}
        </Alert>
      )}
      <InactiveIntegrations />
      {hasRepairAccess && (
        <>
          <HomeScoreWidget />
          <p className="my-2 text-base">{t('dashboard.chooseTaskType')}</p>
          {!!tabs && <TabsHashMenu tabConfigs={tabs} />}
        </>
      )}

      {/*  Comment this for now, optimization for this is needed at a later time. <div className={classNames(`grid grid-cols-${dashboardSettings.columnCount} gap-4`)}>
        {dashboardSettings.showTasksChart && <OpenTasksByTaskTypeChart />}
        {dashboardSettings.showTasksTable && <OpenTaskTable />}
      </div> */}
    </KpcFullPage>
  );
};

export default Dashboard;
